import { Component, DestroyRef, OnInit, signal } from '@angular/core';

import {
  StateService,
  TransitionService,
  UIRouterGlobals,
} from '@uirouter/core';

import { RvSpinnerDirective } from 'src/app/components/loading/rv-spinner.directive';
import { ModalService } from 'src/app/components/modals/modal.service';
import { CompanyAccessService } from 'src/app/settings/services/company-access.service';
import { IntegrationsCompanyDialogsService } from 'src/app/settings/services/integrations/integrations-company/integrations-company-dialogs.service';
import { IntegrationsCompanyService } from 'src/app/settings/services/integrations/integrations-company/integrations-company.service';
import {
  INTEGRATIONS_LIST_STATE,
  INTEGRATIONS_STATE,
  companyIntegrationIdFromRouter,
} from 'src/app/settings/services/integrations/integrations-company/integrations-company.routing';
import { CompanyIntegrationDeepLinkOpenResult } from 'src/app/settings/services/integrations/models/integrations.types';

import { IntegrationsCompanyAddSectionComponent } from './integrations-company-add-section/integrations-company-add-section.component';
import { IntegrationsCompanySectionComponent } from './integrations-company-section/integrations-company-section.component';
import { IntegrationsUserSectionComponent } from './integrations-user-section/integrations-user-section.component';

@Component({
  selector: 'integrations',
  templateUrl: './integrations.component.html',
  styleUrls: ['./integrations.component.scss'],
  standalone: true,
  imports: [
    IntegrationsUserSectionComponent,
    IntegrationsCompanySectionComponent,
    IntegrationsCompanyAddSectionComponent,
    RvSpinnerDirective,
  ],
})
export class IntegrationsComponent implements OnInit {
  /** Shared skeleton state for both company sections while the catalog loads. */
  readonly companyInitialLoading = signal(false);

  /** Spinner on company sections while a field integration save runs. */
  readonly companyActionLoading = signal(false);

  /** Full-page spinner on Integrations while a deeplink waits for the catalog. */
  readonly deepLinkLoading = signal(false);

  /** Shared company catalog load started in ngOnInit; deep links await this. */
  private companyInitialLoadPromise: Promise<void> = Promise.resolve();

  /** Serializes route-driven modal opens so sidebar clicks cannot stack dialogs. */
  private modalSync: Promise<void> | null = null;

  constructor(
    public companyAccessService: CompanyAccessService,
    private integrationsCompanyService: IntegrationsCompanyService,
    private integrationsCompanyDialogsService: IntegrationsCompanyDialogsService,
    private modalService: ModalService,
    private uiRouterGlobals: UIRouterGlobals,
    private stateService: StateService,
    private transitionService: TransitionService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.companyInitialLoadPromise = this.loadCompanyIntegrations();
    void this.companyInitialLoadPromise.catch(() => undefined);

    const deregister = this.transitionService.onSuccess(
      {
        to: (state: { name?: string }) =>
          (state.name ?? '').startsWith(`${INTEGRATIONS_STATE}.`) ||
          state.name === INTEGRATIONS_STATE,
      },
      () => {
        void this.syncModalFromRoute().catch(() => undefined);
      },
    );
    this.destroyRef.onDestroy(() => {
      deregister();
    });

    void this.syncModalFromRoute().catch(() => undefined);
  }

  private async loadCompanyIntegrations(): Promise<void> {
    if (!this.companyAccessService.accessCompanyIntegrations) {
      return;
    }
    this.companyInitialLoading.set(true);
    try {
      await this.integrationsCompanyService.load();
    } finally {
      this.companyInitialLoading.set(false);
    }
  }

  private routeIntegrationId(): string | null {
    return companyIntegrationIdFromRouter(this.uiRouterGlobals.current?.name, {
      integrationId: this.uiRouterGlobals.params['integrationId'],
    });
  }

  private async syncModalFromRoute(): Promise<void> {
    const id = this.routeIntegrationId();
    if (!id) {
      if (this.modalSync) {
        this.modalService.closeAll();
      }
      return this.modalSync ?? Promise.resolve();
    }

    if (this.modalSync) {
      await this.modalSync;
      const nextId = this.routeIntegrationId();
      if (nextId && nextId !== id) {
        return this.syncModalFromRoute();
      }
      return;
    }

    const openedFor = id;
    this.modalSync = this.openFromRoute(id).finally(() => {
      this.modalSync = null;
    });
    await this.modalSync;

    const nextId = this.routeIntegrationId();
    if (nextId && nextId !== openedFor) {
      return this.syncModalFromRoute();
    }
  }

  /**
   * Opens the company integration for the active child route (`/alerts` or
   * `/integrations/:id`). Returns to the list when the dialog closes.
   */
  private async openFromRoute(id: string): Promise<void> {
    this.deepLinkLoading.set(true);
    let catalogLoadFailed = false;
    try {
      await this.companyInitialLoadPromise;
    } catch {
      catalogLoadFailed = true;
    } finally {
      this.deepLinkLoading.set(false);
    }

    if (this.routeIntegrationId() !== id) {
      return;
    }

    if (catalogLoadFailed) {
      await this.showDeepLinkNotFound();
    } else if (!this.companyAccessService.accessCompanyIntegrations) {
      const name = this.integrationsCompanyService.findById(id)?.name;
      await this.showDeepLinkAccessDenied(name);
    } else {
      const result = await this.openIntegrationById(id);
      if (result.kind === 'forbidden') {
        await this.showDeepLinkAccessDenied(result.name);
      } else if (result.kind === 'not-found') {
        await this.showDeepLinkNotFound();
      } else if (result.kind === 'navigating-away') {
        return;
      }
    }

    // `replace` so Back does not land on the deep link and reopen the dialog.
    if (this.routeIntegrationId() === id) {
      await this.stateService.go(
        INTEGRATIONS_LIST_STATE,
        {},
        { location: 'replace' },
      );
    }
  }

  private async openIntegrationById(
    id: string,
  ): Promise<CompanyIntegrationDeepLinkOpenResult> {
    const integration = this.integrationsCompanyService.findById(id);
    if (!integration) {
      return { kind: 'not-found' };
    }
    if (!this.integrationsCompanyService.canAccessIntegration(integration)) {
      return { kind: 'forbidden', name: integration.name };
    }

    const add = !this.integrationsCompanyService
      .connected()
      .some((entry) => entry.id === id);
    const values = await this.integrationsCompanyDialogsService.promptConfigure(
      integration,
      add,
    );

    if (values === 'navigating-away') {
      return { kind: 'navigating-away' };
    }

    if (!values || !integration.enabledKey) {
      return { kind: 'opened' };
    }

    this.companyActionLoading.set(true);
    try {
      await this.integrationsCompanyService.saveIntegrationSettings(
        integration.enabledKey,
        values,
        this.integrationsCompanyService.exclusiveKeysToDisable(integration),
      );
      this.integrationsCompanyDialogsService.showSuccessSnackbar(
        add ? 'Connected' : 'Updated',
        integration.name,
      );
    } catch (error) {
      console.error('Failed to save company integration:', error);
      this.integrationsCompanyDialogsService.showErrorSnackbar(
        `Failed to ${add ? 'connect' : 'update'} ${integration.name} integration.`,
      );
    } finally {
      this.companyActionLoading.set(false);
    }

    return { kind: 'opened' };
  }

  private showDeepLinkAccessDenied(integrationName?: string): Promise<unknown> {
    const target = integrationName
      ? `the ${integrationName} integration`
      : 'this integration';
    return this.modalService.showMessage(
      'Access denied',
      `You don’t have access to ${target}. Please contact your system administrator.`,
    );
  }

  private showDeepLinkNotFound(): Promise<unknown> {
    return this.modalService.showMessage(
      'Integration not found',
      'This integration link is invalid or no longer available.',
    );
  }
}
